import { moveClass, type MoveClass } from "@synapse/engine";

export type { MoveClass };

/**
 * Subset of the game's MoveJudgement needed to classify a move, plus what the
 * checkpoint tip needs to name a word: the word the move arrived at and
 * whether that checkpoint was already spent.
 */
export interface RecentMove {
  isGlobalOptimal: boolean;
  isLocalOptimal: boolean;
  playerChose?: string;
  usedAsCheckpoint?: boolean;
}

/** The tutorial pair's par: long enough for every tip to land on a best-route move. */
export const TUTORIAL_PAR = 5;

export type SegColor = "gold" | "purple" | "green" | "red" | "blue";
export interface Segment {
  text: string;
  color?: SegColor; // undefined = default text color
  icon?: string; // MaterialCommunityIcons name; rendered inline in place of text
}

export interface TutorialGuidance {
  highlightWord: string | null;
  stepsRemaining: number;
  done: boolean;
  segments: Segment[];
  /** Short reaction to the last move ("Gold path!"), shown as a headline. */
  headline?: Segment[];
  /** The tip or distance line under the headline. */
  body?: Segment[];
  /** Her own card (the first one): the banner draws her mark before the headline. */
  ariadne?: true;
}

/**
 * Monotonic tip cursor for one tutorial game. Backtracking cuts the game's
 * optimalChoices back, so the move count alone would rewind and replay tips.
 * `highest` is the most moves reached; a tip only shows on the move that first
 * reached a new count (`newHigh`). `backtracked` marks the last change as a
 * backtrack, so the checkpoint move's flavor ("Gold path!") is not replayed.
 */
export interface TipProgress {
  highest: number;
  lastLength: number;
  newHigh: boolean;
  backtracked: boolean;
}

export const INITIAL_TIP_PROGRESS: TipProgress = {
  highest: 0,
  lastLength: 0,
  newHigh: false,
  backtracked: false,
};

/** Pure + idempotent: same length in, same progress out. */
export const advanceTipProgress = (
  prev: TipProgress,
  length: number,
): TipProgress => {
  if (length === prev.lastLength) return prev;
  if (length > prev.highest) {
    return {
      highest: length,
      lastLength: length,
      newHigh: true,
      backtracked: false,
    };
  }
  return {
    highest: prev.highest,
    lastLength: length,
    newHigh: false,
    backtracked: length < prev.lastLength,
  };
};

const classify = (m: RecentMove): MoveClass => moveClass(m);

const TIPS: Segment[][] = [
  [
    { icon: "gesture-tap-hold", text: "" },
    { text: " Long-press or " },
    { icon: "mouse", text: "" },
    { text: " right-click a word option to peek its definition." },
  ],
  [
    {
      text: "The glowing word is the next step on a best route from here, but you're free to explore.",
    },
  ],
  [
    { text: "Global Optimal (" },
    { text: "gold", color: "gold" },
    { text: ") and Local Optimal (" },
    { text: "purple", color: "purple" },
    { text: ") moves are checkpoints. Click one in your path to " },
    { icon: "step-backward", text: "" },
    { text: " backtrack there." },
  ],
];

/** The last tip (move four): she points at her mark. "Almost there" only when it is true. */
const markTip = (stepsRemaining: number): Segment[] => [
  {
    text: `${stepsRemaining <= 2 ? "I think we're almost there! " : ""}Tap my mark in the header to see my thread any time.`,
  },
];

/** One tip per move: the three static ones, then the mark tip. */
const TIP_COUNT = TIPS.length + 1;

/**
 * Her first card (owner, 2026-10-21): she introduces herself and asks for help.
 * Before the game is set up there is no glowing word to name.
 */
const herIntro = (
  targetSeg: Segment,
  highlightWord: string | null,
): { headline: Segment[]; body: Segment[] } => ({
  headline: [{ text: "I'm Ariadne" }],
  body: [
    {
      text: "Welcome to my mind! I'm a little lost, though. Can you help me reach ",
    },
    targetSeg,
    { text: "? " },
    ...(highlightWord
      ? [
          { text: "I think " },
          { text: `"${highlightWord}"`, color: "blue" as const },
          { text: " might be the right way. " },
        ]
      : []),
    { text: "Try tapping the " },
    { text: "glowing", color: "blue" },
    { text: " word." },
  ],
});

/**
 * The word of the most recent earlier checkpoint the player could backtrack
 * to right now: reached by a Global or Local Optimal move, not yet spent, and
 * not the word they stand on (GameActionsService.backtrackToWord's rule).
 */
const earlierCheckpoint = (moves: RecentMove[]): string | null => {
  for (let i = moves.length - 2; i >= 0; i--) {
    const m = moves[i];
    if (!m.playerChose || m.usedAsCheckpoint) continue;
    if (classify(m) !== "nonOptimal") return m.playerChose;
  }
  return null;
};

/** The checkpoint tip as an invitation when there is a checkpoint to name. */
const checkpointTip = (moves: RecentMove[]): Segment[] => {
  const word = earlierCheckpoint(moves);
  if (!word) return TIPS[2];
  return [
    ...TIPS[2].slice(0, 4),
    { text: `) moves are checkpoints. Try it: tap "${word}" in your path to ` },
    { icon: "step-backward", text: "" },
    { text: " backtrack there." },
  ];
};

/**
 * Derive the walkthrough callout + chip highlight from live game state, reacting
 * to the last move's class (golden = Global Optimal, optimal = Local Optimal
 * and not Global, nonOptimal = Detour; MoveClass values are Ruling R2) and a
 * Detour streak. `suggestedPathFromCurrent` is the Local Optimal route,
 * current-inclusive ([current, …, target]); the glowing next word is index [1].
 * `recentMoves` is the game's move judgements (optimalChoices).
 * `progress` (from advanceTipProgress) keeps tips from replaying after a
 * backtrack; when omitted, the tip follows the move count.
 */
export const tutorialGuidance = (
  currentWord: string | null,
  suggestedPathFromCurrent: string[],
  targetWord: string | null,
  recentMoves: RecentMove[] = [],
  progress?: TipProgress,
): TutorialGuidance => {
  const t = targetWord ?? "the target";
  const targetSeg: Segment = { text: `"${t}"`, color: "red" };
  const stepsRemaining = Math.max(0, suggestedPathFromCurrent.length - 1);
  const remainSegs: Segment[] = [
    {
      text: `${stepsRemaining} ${stepsRemaining === 1 ? "step" : "steps"} to `,
    },
    targetSeg,
    { text: "." },
  ];

  if (!currentWord || !targetWord) {
    const { headline, body } = herIntro(targetSeg, null);
    return {
      highlightWord: null,
      stepsRemaining: 0,
      done: false,
      segments: body,
      headline,
      body,
      ariadne: true,
    };
  }

  if (currentWord === targetWord) {
    // Done card (owner, 2026-10-21; reworded 2026-10-27). She followed this
    // once; from here she plays her own seat and the report says how much of
    // her way was yours (spec §3.2a). She learns from every game, won or not,
    // so this promises play, not wins.
    const headline: Segment[] = [{ text: "Phew! Made it!" }];
    const body: Segment[] = [
      {
        text: "Thank you, hero. From now on I'll try my own way and report back. Every game, I learn a little more from yours.",
      },
    ];
    return {
      highlightWord: null,
      stepsRemaining: 0,
      done: true,
      segments: [...headline, { text: " " }, ...body],
      headline,
      body,
    };
  }

  const highlightWord =
    suggestedPathFromCurrent.length > 1 ? suggestedPathFromCurrent[1] : null;

  if (recentMoves.length === 0) {
    const { headline, body } = herIntro(targetSeg, highlightWord);
    return {
      highlightWord,
      stepsRemaining,
      done: false,
      segments: body,
      headline,
      body,
      ariadne: true,
    };
  }

  const totalMoves = recentMoves.length;

  const last: MoveClass | null = recentMoves.length
    ? classify(recentMoves[recentMoves.length - 1])
    : null;
  let nonOptimalStreak = 0;
  for (let i = recentMoves.length - 1; i >= 0; i--) {
    if (classify(recentMoves[i]) === "nonOptimal") nonOptimalStreak++;
    else break;
  }

  // one tip per move, spread across moves 1..TIP_COUNT (any message can carry
  // one), shown only on the move that first reaches that count
  const showTip = progress ? progress.newHigh : true;
  const tipIndex = totalMoves - 1;
  const tipSegs: Segment[] | null =
    showTip && tipIndex >= 0 && tipIndex < TIP_COUNT
      ? tipIndex === 2
        ? checkpointTip(recentMoves)
        : tipIndex === TIPS.length
          ? markTip(stepsRemaining)
          : TIPS[tipIndex]
      : null;

  let flavor: Segment[];
  if (progress?.backtracked && last !== null) {
    flavor = [
      { icon: "step-backward", text: "" },
      { text: ` Backtracked to "${currentWord}".` },
    ];
  } else if (last === "golden") {
    flavor = [{ text: "Gold path!", color: "gold" }];
  } else if (last === "optimal") {
    flavor = [{ text: "Good move!", color: "purple" }];
  } else if (nonOptimalStreak === 2) {
    flavor = [{ text: "Bold strategy. The gold path misses you." }];
  } else if (totalMoves > 2) {
    // dialed down after the first couple of moves: just the distance
    flavor = [];
  } else {
    flavor = [
      { text: "That works too. Exploring is fine, just not a best route." },
    ];
  }

  const tail: Segment[] = tipSegs
    ? [{ text: " " }, ...tipSegs]
    : [{ text: " " }, ...remainSegs];
  const segments = flavor.length
    ? [...flavor, ...tail]
    : (tipSegs ?? remainSegs);
  const headline = flavor;
  const body = tipSegs ?? remainSegs;

  return {
    highlightWord,
    stepsRemaining,
    done: false,
    segments,
    headline,
    body,
  };
};
